// /src/components/EditEmployee.jsx
import React, { useEffect, useCallback, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import Select from "react-select";

import {
  useGetEmployeeByIdQuery,
  useUpdateEmployeeMutation,
  useActivateEmployeeMutation,
  useDeactivateEmployeeMutation,
} from "../toolkitServices/employeeApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetCountersQuery } from "../toolkitServices/counterApi";
import { useGetAllRolesQuery } from "../toolkitServices/roleApi";
import { useGetAllDepartmentsQuery } from "../toolkitServices/departmentApi";
import { indianStates } from "../utils/helpers";

/* ---------- Shared UI tokens ---------- */
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

/* ---------- helpers ---------- */
const idOf = (x) => (x && typeof x === "object" ? x._id : x) || "";
const onlyDigits = (v = "") => v.toString().replace(/\D/g, "");

const EditEmployee = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [step, setStep] = useState(1);

  // ✅ NEW: hydration guard (fixes counters/defaultCounter wiping on edit)
  const [isHydrated, setIsHydrated] = useState(false);

  const { data: branchesResp } = useGetAllBranchesQuery();
  const { data: countersResp } = useGetCountersQuery();
  const { data: roles } = useGetAllRolesQuery();
  const { data: departments } = useGetAllDepartmentsQuery();

  /* ---------- Data ---------- */
  const {
    data: resp,
    isLoading: isLoadingEmp,
    isFetching,
    isError: loadError,
    error: loadErrObj,
  } = useGetEmployeeByIdQuery(id, { skip: !id });

  const row = resp?.data || resp;

  /* ---------- Mutations ---------- */
  const [updateEmployee, { isLoading: isSaving, error: updError }] =
    useUpdateEmployeeMutation();
  const [activateEmp, { isLoading: isActivating }] =
    useActivateEmployeeMutation();
  const [deactivateEmp, { isLoading: isDeactivating }] =
    useDeactivateEmployeeMutation();

  /* ---------- react-select options ---------- */
  const branchOptions = useMemo(() => {
    const list = branchesResp?.data || [];
    return list.map((b) => ({ value: b._id, label: b.branchName }));
  }, [branchesResp]);

  /* ---------- react-select styles ---------- */
  const rsStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 42,
        borderRadius: 8,
        borderColor: state?.isFocused ? "#9481ce" : "#d1d5db",
        boxShadow: "none",
        fontSize: 14,
        ":hover": { borderColor: "#9481ce" },
      }),
      valueContainer: (base) => ({
        ...base,
        paddingTop: 4,
        paddingBottom: 4,
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
    }),
    []
  );

  /* ---------- Form ---------- */
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
    reset,
    setFocus,
    watch,
    control,
    setValue,
  } = useForm({
    mode: "onBlur",
    shouldUnregister: false,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      officialEmail: "",
      contact: "",
      adhar: "",
      pan: "",
      dob: "",
      gender: "male",

      addressLine1: "",
      city: "",
      pincode: "",
      state: "Maharashtra",
      country: "india",

      // ✅ multi branches
      branches: [],
      // ✅ default branch
      branch: null,

      department: "",

      // ✅ counters (multi + default)
      counters: [],
      counter: null,

      role: "",
      salary: "",

      employeeCommission: "",

      bankName: "",
      accountHolderName: "",
      accountNumber: "",
      accountBranch: "",
      ifscCode: "",
    },
  });

  const selectedBranches = watch("branches"); // array of {value,label}
  const defaultBranch = watch("branch"); // {value,label}

  const selectedCounters = watch("counters"); // array of {value,label}
  const defaultCounter = watch("counter"); // {value,label}

  // ✅ selected branch ids set (MULTI)
  const selectedBranchIdSet = useMemo(() => {
    const ids = Array.isArray(selectedBranches)
      ? selectedBranches.map((b) => b?.value).filter(Boolean)
      : [];
    return new Set(ids.map((x) => String(x)));
  }, [selectedBranches]);

  // ✅ default branch dropdown options: only selected branches
  const defaultBranchOptions = useMemo(() => {
    if (!Array.isArray(selectedBranches) || selectedBranches.length === 0)
      return [];
    return selectedBranches;
  }, [selectedBranches]);

  // ✅ counters options (filter by selected branches, NOT default branch)
  const counterOptions = useMemo(() => {
    const list = countersResp?.data || [];
    if (!selectedBranchIdSet || selectedBranchIdSet.size === 0) return [];
    const filtered = list.filter((c) =>
      selectedBranchIdSet.has(String(c.branchId))
    );
    return filtered.map((c) => ({ value: c._id, label: c.counterName }));
  }, [countersResp, selectedBranchIdSet]);

  // ✅ default counter options: only from selectedCounters
  const defaultCounterOptions = useMemo(() => {
    return Array.isArray(selectedCounters) ? selectedCounters : [];
  }, [selectedCounters]);

  // ✅ when id changes, reset hydration (important when navigating between edits)
  useEffect(() => {
    setIsHydrated(false);
  }, [id]);

  /* ---------- Hydrate form from API ---------- */
  useEffect(() => {
    if (!row || isFetching) return;

    const rowBranchId = idOf(row.branch);
    const rowBranches = Array.isArray(row.branches) ? row.branches : [];

    const mappedBranches = rowBranches
      .map((b) => ({
        value: idOf(b),
        label: b?.branchName || "Branch",
      }))
      .filter((x) => x.value);

    const mappedDefaultBranch =
      rowBranchId && mappedBranches.length
        ? mappedBranches.find((b) => b.value === rowBranchId) ||
          mappedBranches[0]
        : rowBranchId
        ? {
            value: rowBranchId,
            label:
              (typeof row.branch === "object" && row.branch?.branchName) ||
              "Branch",
          }
        : null;

    // ✅ counters hydrate
    const rowCounterId = idOf(row.counter);
    const rowCounters = Array.isArray(row.counters) ? row.counters : [];

    const mappedCounters = rowCounters
      .map((c) => ({
        value: idOf(c),
        label: c?.counterName || "Counter",
      }))
      .filter((x) => x.value);

    const mappedDefaultCounter =
      rowCounterId && mappedCounters.length
        ? mappedCounters.find((c) => c.value === rowCounterId) ||
          mappedCounters[0]
        : rowCounterId
        ? {
            value: rowCounterId,
            label:
              (typeof row.counter === "object" && row.counter?.counterName) ||
              "Counter",
          }
        : null;

    reset({
      firstName: row.firstName || "",
      lastName: row.lastName || "",
      email: row.email || "",
      officialEmail: row.officialEmail || "",
      contact: row.contact || "",
      adhar: row.adhar || "",
      pan: row.pan || "",
      dob: row.dob || "",
      gender: row.gender || "male",

      addressLine1: row.addressLine1 || "",
      city: row.city || "",
      pincode: row.pincode || "",
      state: row.state || "Maharashtra",
      country: row.country || "india",

      branches: mappedBranches,
      branch: mappedDefaultBranch,

      department: idOf(row.department),

      counters: mappedCounters,
      counter: mappedDefaultCounter,

      role: idOf(row.role),
      salary: row.salary || "",

      employeeCommission:
        row.employeeCommission === 0 || row.employeeCommission
          ? String(row.employeeCommission)
          : "",

      bankName: row.bankName || "",
      accountHolderName: row.accountHolderName || "",
      accountNumber: row.accountNumber || "",
      accountBranch: row.accountBranch || "",
      ifscCode: row.ifscCode || "",
    });

    setStep(1);
    requestAnimationFrame(() => setFocus("firstName"));

    // ✅ allow other effects AFTER hydration is done
    setIsHydrated(true);
  }, [row, isFetching, reset, setFocus]);

  // ✅ if user removes a branch that was selected as default -> auto-fix default branch
  useEffect(() => {
    if (!isHydrated) return;
    if (!Array.isArray(selectedBranches)) return;

    const defaultId = defaultBranch?.value || "";
    if (!defaultId) {
      if (selectedBranches.length > 0) {
        setValue("branch", selectedBranches[0], { shouldDirty: true });
      }
      return;
    }

    const stillExists = selectedBranches.some((b) => b.value === defaultId);
    if (!stillExists) {
      setValue("branch", selectedBranches[0] || null, { shouldDirty: true });
    }
  }, [isHydrated, selectedBranches, defaultBranch, setValue]);

  // ✅ Branches change => counters auto-trim (NOT full reset)
  useEffect(() => {
    if (!isHydrated) return;
    if (!countersResp?.data) return;

    const allCounters = countersResp?.data || [];

    // no branches => clear counters + default counter
    if (!selectedBranchIdSet || selectedBranchIdSet.size === 0) {
      setValue("counters", [], { shouldDirty: true, shouldValidate: true });
      setValue("counter", null, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const allowedCounterIdSet = new Set(
      allCounters
        .filter((c) => selectedBranchIdSet.has(String(c.branchId)))
        .map((c) => String(c._id))
    );

    const currentSelected = Array.isArray(selectedCounters)
      ? selectedCounters
      : [];
    const trimmedSelected = currentSelected.filter((opt) =>
      allowedCounterIdSet.has(String(opt?.value))
    );

    // update only if changed
    const changed =
      trimmedSelected.length !== currentSelected.length ||
      trimmedSelected.some(
        (x, i) => String(x?.value) !== String(currentSelected?.[i]?.value)
      );

    if (changed) {
      setValue("counters", trimmedSelected, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    // ensure defaultCounter still valid
    if (defaultCounter?.value) {
      const ok = allowedCounterIdSet.has(String(defaultCounter.value));
      if (!ok) {
        setValue("counter", null, { shouldDirty: true, shouldValidate: true });
      }
    }
  }, [
    isHydrated,
    countersResp,
    selectedBranchIdSet,
    selectedCounters,
    defaultCounter,
    setValue,
  ]);

  // ✅ if user removes a counter that was selected as default -> auto-fix default counter
  useEffect(() => {
    if (!isHydrated) return;
    if (!Array.isArray(selectedCounters)) return;

    const defaultId = defaultCounter?.value || "";
    if (!defaultId) {
      if (selectedCounters.length > 0) {
        setValue("counter", selectedCounters[0], { shouldDirty: true });
      }
      return;
    }

    const stillExists = selectedCounters.some((c) => c.value === defaultId);
    if (!stillExists) {
      setValue("counter", selectedCounters[0] || null, { shouldDirty: true });
    }
  }, [isHydrated, selectedCounters, defaultCounter, setValue]);

  /* ---------- Step focus ---------- */
  useEffect(() => {
    const idRAF = requestAnimationFrame(() => {
      if (step === 1) setFocus("firstName");
      if (step === 2) setFocus("addressLine1");
      if (step === 3) setFocus("branches");
      if (step === 4) setFocus("bankName");
    });
    return () => cancelAnimationFrame(idRAF);
  }, [step, setFocus]);

  /* ---------- Keyboard shortcuts ---------- */
  const nextStep = useCallback(async () => {
    const ok = await trigger();
    if (ok) setStep((s) => (s < 4 ? s + 1 : s));
  }, [trigger]);

  const prevStep = useCallback(() => setStep((s) => (s > 1 ? s - 1 : s)), []);

  useEffect(() => {
    const handler = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const isBack = (e.ctrlKey || e.metaKey) && e.key === "Backspace";
      const isPrev = e.altKey && e.key === "ArrowLeft";

      if (isSave) {
        e.preventDefault();
        if (step === 4) handleSubmit(onSubmit)();
        else nextStep();
      }
      if (isBack) {
        e.preventDefault();
        navigate(-1);
      }
      if (isPrev) {
        e.preventDefault();
        prevStep();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [step, handleSubmit, nextStep, prevStep, navigate]);

  /* ---------- Error toasts ---------- */
  useEffect(() => {
    if (loadError) {
      const msg =
        loadErrObj?.data?.msg ||
        loadErrObj?.data?.message ||
        loadErrObj?.error ||
        "Failed to load employee";
      toast.error(msg, { position: "top-center" });
    }
  }, [loadError, loadErrObj]);

  useEffect(() => {
    if (updError) {
      const msg =
        updError?.data?.msg ||
        updError?.data?.message ||
        updError?.error ||
        "Failed to update employee";
      toast.error(msg, { position: "top-center" });
    }
  }, [updError]);

  const isActive = !!row?.isActive;
  const busy = isSaving || isSubmitting || isFetching || isLoadingEmp;

  /* ---------- Submit ---------- */
  const onSubmit = async (form) => {
    // branches
    const branchIds = Array.isArray(form.branches)
      ? form.branches.map((x) => x?.value).filter(Boolean)
      : [];
    const defaultBranchId = form.branch?.value || branchIds[0] || "";

    // counters
    const counterIds = Array.isArray(form.counters)
      ? form.counters.map((x) => x?.value).filter(Boolean)
      : [];
    const defaultCounterId = form.counter?.value || counterIds[0] || "";

    const payload = {
      firstName: form.firstName?.trim(),
      lastName: form.lastName?.trim() || "",
      email: (form.email || "").trim(),
      officialEmail: (form.officialEmail || "").trim(),

      contact: onlyDigits(form.contact || ""),
      adhar: onlyDigits(form.adhar || ""),
      pan: (form.pan || "").trim().toUpperCase(),
      dob: form.dob || "",
      gender: form.gender,

      addressLine1: (form.addressLine1 || "").trim(),
      city: (form.city || "").trim(),
      pincode: onlyDigits(form.pincode || ""),
      state: form.state,
      country: form.country,

      branch: defaultBranchId,
      branches: branchIds,

      counter: defaultCounterId,
      counters: counterIds,

      department: form.department,
      role: form.role,
      salary: (form.salary || "").trim(),

      employeeCommission: String(form.employeeCommission ?? "").trim(),

      bankName: (form.bankName || "").trim(),
      accountHolderName: (form.accountHolderName || "").trim(),
      accountNumber: onlyDigits(form.accountNumber || ""),
      accountBranch: (form.accountBranch || "").trim(),
      ifscCode: (form.ifscCode || "").trim().toUpperCase(),
    };

    try {
      await updateEmployee({ id, ...payload }).unwrap();
      toast.success("Employee updated successfully!", {
        position: "top-center",
      });
      navigate("/members/employee-list");
    } catch (e) {
      const msg =
        e?.data?.msg || e?.data?.message || e?.error || "Failed to update";
      toast.error(msg, { position: "top-center" });
    }
  };

  /* ---------- Activate/Deactivate ---------- */
  const toggleActive = async () => {
    if (row?.isActive === undefined) return;
    try {
      if (row.isActive) {
        await deactivateEmp(id).unwrap();
        toast.success("Employee deactivated", { position: "top-center" });
      } else {
        await activateEmp(id).unwrap();
        toast.success("Employee activated", { position: "top-center" });
      }
    } catch (e) {
      toast.error(e?.data?.msg || e?.error || "Action failed", {
        position: "top-center",
      });
    }
  };

  return (
    <div className="px-4 py-4">
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/members/employee-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Employee List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>

          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
                  Edit Employee
                </h1>
                <p className="text-xs text-gray-500">
                  Tips:{" "}
                  <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
                  <kbd className="px-1 bg-gray-100 rounded border">S</kbd>{" "}
                  save/next,{" "}
                  <kbd className="px-1 bg-gray-100 rounded border ml-1">
                    Ctrl
                  </kbd>
                  +
                  <kbd className="px-1 bg-gray-100 rounded border">
                    Back Spc
                  </kbd>{" "}
                  back,{" "}
                  <kbd className="px-1 bg-gray-100 rounded border ml-1">
                    Alt
                  </kbd>
                  +<kbd className="px-1 bg-gray-100 rounded border">←</kbd> prev
                  step
                </p>
              </div>

              <div className="hidden sm:flex items-center gap-3 pr-2">
                <span
                  className={[
                    "text-xs px-2 py-1 rounded-full",
                    isActive
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700",
                  ].join(" ")}
                >
                  {isActive ? "Active" : "Inactive"}
                </span>
                <button
                  type="button"
                  onClick={toggleActive}
                  disabled={isActivating || isDeactivating || isLoadingEmp}
                  className="text-xs px-3 py-1.5 rounded border border-[#9481ce] text-[#9481ce] hover:bg-[#9481ce]/10 disabled:opacity-60"
                >
                  {isActive ? "Deactivate" : "Activate"}
                </button>

                <span className="text-xs text-gray-500">Step {step} of 4</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {/* Loading overlay */}
        {busy && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        {/* API error banner */}
        {(loadError || updError) && (
          <div className="mx-5 mt-5 mb-1 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {loadErrObj?.data?.msg ||
              updError?.data?.msg ||
              loadErrObj?.data?.message ||
              updError?.data?.message ||
              loadErrObj?.error ||
              updError?.error ||
              "Something went wrong. Please try again."}
          </div>
        )}

        <form className="p-5" onSubmit={(e) => e.preventDefault()}>
          {/* STEP 1 */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="First Name" required error={errors.firstName}>
                <input
                  {...register("firstName", {
                    required: "First name is required",
                  })}
                  placeholder="Enter first name"
                  className={`${baseInput} ${
                    errors.firstName ? errRing : okRing
                  }`}
                />
                {errors.firstName?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.firstName.message}
                  </p>
                )}
              </Field>

              <Field label="Last Name" required error={errors.lastName}>
                <input
                  {...register("lastName", {
                    required: "Last name is required",
                  })}
                  placeholder="Enter last name"
                  className={`${baseInput} ${
                    errors.lastName ? errRing : okRing
                  }`}
                />
                {errors.lastName?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.lastName.message}
                  </p>
                )}
              </Field>

              <Field label="Email" required error={errors.email}>
                <input
                  type="email"
                  {...register("email", { required: "Email is required" })}
                  placeholder="Enter email"
                  className={`${baseInput} ${errors.email ? errRing : okRing}`}
                />
                {errors.email?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.email.message}
                  </p>
                )}
              </Field>

              <Field label="Official Email">
                <input
                  type="email"
                  {...register("officialEmail")}
                  placeholder="Enter official email"
                  className={`${baseInput} ${okRing}`}
                  disabled={true}
                />
              </Field>

              <Field label="Contact" required error={errors.contact}>
                <input
                  {...register("contact", {
                    required: "Contact is required",
                    validate: (v) =>
                      /^\d{10}$/.test(v || "") ||
                      "Contact must be exactly 10 digits",
                  })}
                  placeholder="10-digit mobile"
                  className={`${baseInput} ${
                    errors.contact ? errRing : okRing
                  }`}
                />
                {errors.contact?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.contact.message}
                  </p>
                )}
              </Field>

              <Field label="Aadhar" error={errors.adhar}>
                <input
                  {...register("adhar", {
                    validate: (v) =>
                      v === "" ||
                      /^\d{12}$/.test(v) ||
                      "Aadhar must be 12 digits",
                  })}
                  placeholder="12-digit Aadhar (optional)"
                  className={`${baseInput} ${errors.adhar ? errRing : okRing}`}
                />
                {errors.adhar?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.adhar.message}
                  </p>
                )}
              </Field>

              <Field label="PAN" error={errors.pan}>
                <input
                  {...register("pan", {
                    validate: (v) =>
                      v === "" ||
                      /^[A-Z]{5}[0-9]{4}[A-Z]$/.test((v || "").toUpperCase()) ||
                      "PAN must be in format AAAAA9999A",
                  })}
                  placeholder="PAN (optional)"
                  className={`${baseInput} ${errors.pan ? errRing : okRing}`}
                />
                {errors.pan?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.pan.message}
                  </p>
                )}
              </Field>

              <Field label="DOB">
                <input
                  type="date"
                  {...register("dob")}
                  className={`${baseInput} ${okRing}`}
                />
              </Field>

              <Field label="Gender" required error={errors.gender}>
                <select
                  {...register("gender", { required: "Gender is required" })}
                  className={`${baseInput} ${errors.gender ? errRing : okRing}`}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                {errors.gender?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.gender.message}
                  </p>
                )}
              </Field>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Address Line 1" span>
                <input
                  {...register("addressLine1")}
                  placeholder="Street / area"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>

              <Field label="City">
                <input
                  {...register("city")}
                  placeholder="City"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>

              <Field label="Pincode" error={errors.pincode}>
                <input
                  {...register("pincode", {
                    validate: (v) =>
                      v === "" ||
                      /^\d{6}$/.test(v) ||
                      "Pincode must be 6 digits",
                  })}
                  placeholder="6-digit pincode"
                  className={`${baseInput} ${
                    errors.pincode ? errRing : okRing
                  }`}
                />
                {errors.pincode?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.pincode.message}
                  </p>
                )}
              </Field>

              <Field label="State" required error={errors.state}>
                <select
                  {...register("state", { required: "State is required" })}
                  className={`${baseInput} ${errors.state ? errRing : okRing}`}
                >
 <option value="">Select State</option>

                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}                </select>
              </Field>

              <Field label="Country" required error={errors.country}>
                <select
                  {...register("country", { required: "Country is required" })}
                  className={`${baseInput} ${
                    errors.country ? errRing : okRing
                  }`}
                >
                  <option value="india">India</option>
                </select>
              </Field>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              {/* Branches multi */}
              <Field label="Branches" required error={errors.branches}>
                <Controller
                  name="branches"
                  control={control}
                  rules={{
                    validate: (v) =>
                      (Array.isArray(v) && v.length > 0) ||
                      "At least one branch is required",
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isMulti
                      options={branchOptions}
                      placeholder="Select branch(es)"
                      closeMenuOnSelect={false}
                      classNamePrefix="rs"
                      menuPortalTarget={document.body}
                      styles={rsStyles}
                      onChange={(val) => field.onChange(val)}
                      value={field.value}
                    />
                  )}
                />
                {errors.branches?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.branches.message}
                  </p>
                )}
              </Field>

              {/* Default branch single */}
              <Field label="Default Branch" required error={errors.branch}>
                <Controller
                  name="branch"
                  control={control}
                  rules={{
                    validate: (v) =>
                      (!!v && !!v.value) || "Default branch is required",
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={defaultBranchOptions}
                      placeholder={
                        defaultBranchOptions.length
                          ? "Select default branch"
                          : "Select branches first"
                      }
                      isDisabled={defaultBranchOptions.length === 0}
                      classNamePrefix="rs"
                      menuPortalTarget={document.body}
                      styles={rsStyles}
                      onChange={(val) => field.onChange(val)}
                      value={field.value}
                    />
                  )}
                />
                {errors.branch?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.branch.message}
                  </p>
                )}
              </Field>

              <SelectField
                label="Department"
                name="department"
                required
                data={departments?.data}
                field="departmentName"
                register={register}
                errors={errors.department}
              />

              {/* Counters (Multi) */}
              <Field label="Counters" required error={errors.counters}>
                <Controller
                  name="counters"
                  control={control}
                  rules={{
                    validate: (v) =>
                      (Array.isArray(v) && v.length > 0) ||
                      "At least one counter is required",
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isMulti
                      options={counterOptions}
                      placeholder={
                        (selectedBranches?.length || 0) > 0
                          ? "Select counters"
                          : "Select branches first"
                      }
                      isDisabled={(selectedBranches?.length || 0) === 0}
                      closeMenuOnSelect={false}
                      classNamePrefix="rs"
                      menuPortalTarget={document.body}
                      styles={rsStyles}
                      onChange={(val) => field.onChange(val)}
                      value={field.value}
                    />
                  )}
                />
                {(selectedBranches?.length || 0) === 0 ? (
                  <p className="text-[11px] text-gray-500 mt-1">
                    (Branches select karo to counters aayenge)
                  </p>
                ) : null}
                {errors.counters?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.counters.message}
                  </p>
                )}
              </Field>

              {/* Default Counter (Single) */}
              <Field label="Default Counter" required error={errors.counter}>
                <Controller
                  name="counter"
                  control={control}
                  rules={{
                    validate: (v) =>
                      (!!v && !!v.value) || "Default counter is required",
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={defaultCounterOptions}
                      placeholder={
                        defaultCounterOptions.length
                          ? "Select default counter"
                          : "Select counters first"
                      }
                      isDisabled={defaultCounterOptions.length === 0}
                      classNamePrefix="rs"
                      menuPortalTarget={document.body}
                      styles={rsStyles}
                      onChange={(val) => field.onChange(val)}
                      value={field.value}
                    />
                  )}
                />
                {errors.counter?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.counter.message}
                  </p>
                )}
              </Field>

              <SelectField
                label="Role"
                name="role"
                required
                data={roles?.data}
                field="roleName"
                register={register}
                errors={errors.role}
              />

              <Field label="Salary">
                <input
                  {...register("salary")}
                  placeholder="Salary (optional)"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>

              {/* Commission */}
              <Field
                label="Employee Commission"
                error={errors.employeeCommission}
              >
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  {...register("employeeCommission", {
                    validate: (v) => {
                      if (v === "" || v == null) return true;
                      const n = Number(v);
                      if (Number.isNaN(n)) return "Only number allowed";
                      if (n < 0) return "Minus value not allowed";
                      return true;
                    },
                  })}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e" || e.key === "E")
                      e.preventDefault();
                  }}
                  disabled={true}
                  placeholder="Commission (e.g. 1.5)"
                  className={`${baseInput} ${
                    errors.employeeCommission ? errRing : okRing
                  }`}
                />
                {errors.employeeCommission?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.employeeCommission.message}
                  </p>
                )}
              </Field>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Bank Name">
                <input
                  {...register("bankName")}
                  placeholder="Enter bank name"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>
              <Field label="Account Holder Name">
                <input
                  {...register("accountHolderName")}
                  placeholder="Enter account holder name"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>
              <Field label="Account Number" error={errors.accountNumber}>
                <input
                  {...register("accountNumber", {
                    validate: (v) =>
                      v === "" ||
                      /^\d{9,18}$/.test(v) ||
                      "Account number must be 9 to 18 digits",
                  })}
                  placeholder="Enter account number"
                  className={`${baseInput} ${
                    errors.accountNumber ? errRing : okRing
                  }`}
                />
                {errors.accountNumber?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.accountNumber.message}
                  </p>
                )}
              </Field>
              <Field label="Branch">
                <input
                  {...register("accountBranch")}
                  placeholder="Enter branch"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>
              <Field label="IFSC Code" error={errors.ifscCode}>
                <input
                  {...register("ifscCode", {
                    validate: (v) =>
                      v === "" ||
                      /^[A-Z]{4}0[A-Z0-9]{6}$/.test((v || "").toUpperCase()) ||
                      "Invalid IFSC code format (e.g., SBIN0000456)",
                  })}
                  placeholder="Enter IFSC"
                  className={`${baseInput} ${
                    errors.ifscCode ? errRing : okRing
                  }`}
                />
                {errors.ifscCode?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.ifscCode.message}
                  </p>
                )}
              </Field>
            </div>
          )}

          {/* Footer actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-5">
            <motion.button
              whileHover={{ scale: busy ? 1 : 1.01 }}
              whileTap={{ scale: busy ? 1 : 0.98 }}
              type="button"
              onClick={prevStep}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
              disabled={step === 1 || busy}
              title="Previous step"
            >
              Previous
            </motion.button>

            <motion.button
              whileHover={{ scale: busy ? 1 : 1.01 }}
              whileTap={{ scale: busy ? 1 : 0.98 }}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (step < 4) nextStep();
                else handleSubmit(onSubmit)();
              }}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={busy}
              title={step === 4 ? "Update (Ctrl+S)" : "Next (Ctrl+S)"}
            >
              {busy ? "Saving..." : step === 4 ? "Update (Ctrl+S)" : "Next"}
            </motion.button>

            <Link
              to="/members/employee-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

/* ---------- small reusable field components ---------- */
const Field = ({ label, required, error, children, span }) => (
  <div className={span ? "md:col-span-3" : ""}>
    <label className="block text-[13px] font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const SelectField = ({
  label,
  name,
  required,
  data,
  field,
  register,
  errors,
  disabled = false,
  helperText = "",
}) => (
  <Field label={label} required={required} error={errors}>
    <select
      {...register(name, required ? { required: `${label} is required` } : {})}
      disabled={disabled}
      className={`${baseInput} ${errors ? errRing : okRing} ${
        disabled ? "bg-gray-100 cursor-not-allowed" : ""
      }`}
    >
      <option value="">Select {label.toLowerCase()}</option>
      {data?.map((item) => (
        <option key={item._id} value={item._id}>
          {item[field]}
        </option>
      ))}
    </select>
    {helperText ? (
      <p className="text-[11px] text-gray-500 mt-1">{helperText}</p>
    ) : null}
  </Field>
);

export default EditEmployee;
